import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import React from "react";

import { ContactPersonRolesEnum, ContactPersonRolesEnumLabel, ContactPersons, useDeleteCompanyCommunicationMutation, useDeleteUsersCommunicationsMutation, usePostNewCompanyCommunicationMutation } from "../../../store/services/communicationService";

interface EditCompaniesCommunicationProps {
  handleClose: () => void;
  customerNo: number;
  person: ContactPersons;
}

export default function EditCompaniesCommunication(
  props: EditCompaniesCommunicationProps
): React.JSX.Element {
  const { handleClose, customerNo, person } = props;
  const [firstName, setFirstname] = React.useState(person.Firstname ?? "");
  const [lastName, setLastname] = React.useState(person.Lastname ?? "");

  const [role, setRole] = React.useState<number[]>(person.Roles ?? []);
  const [responsible, setResponsible] = React.useState<boolean>(
    person.IsResponsible ?? false
  );
  const theme = useTheme();

  const ArrayOfRoles = Object.keys(ContactPersonRolesEnum)
    .filter((key: string) =>
      isNaN(
        Number(
          ContactPersonRolesEnum[key as keyof typeof ContactPersonRolesEnum]
        )
      )
    )
    .map((x) => +x);

  const handleRoleChange = (event: SelectChangeEvent<number[]>) => {
    setRole(event.target.value as number[]);
  };

  const [editContact, { isLoading }] = usePostNewCompanyCommunicationMutation();
  const [deleteContact, { isLoading: deleteContactIsLoading }] =
    useDeleteCompanyCommunicationMutation();

  const [deleteCommunications, { isLoading: deleteCommunicationIsLoading }] =
    useDeleteUsersCommunicationsMutation();

  const handleDelete = async () => {
    if (person.ContactPersonId != null) {
      try {
        await deleteCommunications({
          customerNo,
          customerCommunication: person.Communications ?? [],
        }).unwrap();

        await deleteContact({
          customerId: customerNo,
          contactPersonId: person.ContactPersonId,
        }).unwrap();

        handleClose();
      } catch (error) {
        console.error("Failed to delete contact or communications", error);
      }
    }
  };

  const handleSave = () => {
    if (person.ContactPersonId != null) {
      const data = {
        ContactPersonId: person.ContactPersonId,
        Firstname: firstName,
        Lastname: lastName,
        Email: person.Email,
        Phone: person.Phone,
        Roles: role,
        IsResponsible: responsible,
        Communications: [],
      };
      editContact({ customerId: customerNo, data })
        .unwrap()
        .then(() => {
          handleClose();
        })
        .catch((err) => console.log(err));
    }
  };

  const notOkToEdit = () => {
    if (
      firstName === person.Firstname &&
      lastName === person.Lastname &&
      role === person.Roles &&
      responsible === person.IsResponsible
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.light }}>
        Redigera företagskontakt
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ paddingTop: "25px" }}>
          <TextField
            autoFocus
            margin="dense"
            size="small"
            value={firstName}
            id="name"
            label="Förnamn"
            type="text"
            onChange={(e) => setFirstname(e.target.value)}
            fullWidth
            variant="outlined"
          />
          <TextField
            margin="dense"
            size="small"
            value={lastName}
            id="lastname"
            label="Efternamn"
            type="text"
            onChange={(e) => setLastname(e.target.value)}
            fullWidth
            variant="outlined"
          />
          <FormControl fullWidth>
            <InputLabel>Roller</InputLabel>
            <Select
              multiple
              value={role}
              input={<OutlinedInput label="Roller" />}
              onChange={handleRoleChange}
              renderValue={(selected) =>
                selected
                  .map(
                    (x) =>
                      ContactPersonRolesEnumLabel[
                        x as keyof typeof ContactPersonRolesEnumLabel
                      ]
                  )
                  .join(", ")
              }
            >
              {ArrayOfRoles.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox
                    checked={role.find((x) => x === name) !== undefined}
                  />
                  <ListItemText
                    primary={
                      ContactPersonRolesEnumLabel[
                        name as keyof typeof ContactPersonRolesEnumLabel
                      ]
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onClick={() => setResponsible(!responsible)}
                  checked={responsible}
                />
              }
              label="Avtalsansvarig"
            />
          </FormGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"}>
          <IconButton
            onClick={handleDelete}
            sx={{ position: "absolute", left: 5 }}
          >
            {deleteContactIsLoading || deleteCommunicationIsLoading ? (
              <CircularProgress size={14} />
            ) : (
              <DeleteForeverIcon color="error" />
            )}
          </IconButton>
          <Stack spacing={1} justifyContent={"flex-end"} direction={"row"}>
            <Button color="secondary" variant="outlined" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              disabled={notOkToEdit() || isLoading}
              startIcon={isLoading ? <CircularProgress size={14} /> : null}
              color="primary"
              variant="contained"
              onClick={handleSave}
            >
              Spara
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
